import { useState } from 'react';
import { Agent } from "../../../types/agentTypes";
import QRSection from "../subComponents/QRSection";

function ConnectionCard({
    title,
    icon,
    status,
    onConnect,
    isSelected = false
}: {
    title: string;
    icon: string;
    status: 'connected' | 'disconnected' | 'pending';
    onConnect?: () => void;
    isSelected?: boolean;
}) {
    const getStatusText = () => {
        switch (status) {
            case 'connected':
                return 'Conectado';
            case 'pending':
                return 'Conectar';
            default:
                return 'Conectar';
        }
    };
    const getStatusColor = () => {
        switch (status) {
            case 'connected':
                return 'text-[#00A8FF]';
            case 'pending':
                return 'text-[#00FF7A]';
            default:
                return 'text-[#00FF7A]';
        }
    };
    const getBorderClass = () => {
        return isSelected ? 'border-[#00FF7A]' : 'border-transparent';
    };
    return (
        <article className={`border bg-neutral-100 flex grow flex-col text-sm font-medium whitespace-nowrap w-full px-5 py-[18px] rounded-[10px] border-solid ${getBorderClass()} hover:border-[#00FF7A] transition-colors cursor-pointer`}>
            <img src={icon} alt={`${title} icon`} className="aspect-[1] object-contain w-6" />
            <h3 className="text-[#58585E] mt-2">{title}</h3>
            <button onClick={onConnect} className={`${getStatusColor()} mt-[22px] text-left hover:underline transition-colors`}>
                {getStatusText()}
            </button>
        </article>
    );
}



interface AgentConnectProps {
    agent: Agent | null;
}

function AgentConnect({ agent: _agent }: AgentConnectProps) {
    const [selectedConnection, setSelectedConnection] = useState<string>('whatsapp');
    const [apiKey, setApiKey] = useState('');
    const [orgId, setOrgId] = useState('');
    const [baseUrl, setBaseUrl] = useState('');

    const handleWhatsAppConnect = () => {
        setSelectedConnection('whatsapp');
    };
    const handleChatGPTConnect = () => {
        setSelectedConnection('chatgpt');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    return (
        <main className="w-full ml-[18px] mt-[26px] max-md:max-w-full">
            <div className="gap-5 flex flex-row max-md:flex-col max-md:items-stretch">
                {/** Left Section */}
                <div className="w-[80%] md:w-[42%] max-md:flex-1">
                    <section className="flex flex-col items-stretch max-md:max-w-full max-md:mt-10">
                        <h2 className="text-[#58585E] text-lg font-bold">Conectar</h2>
                        <div className="flex w-full flex-col items-stretch mt-[21px] pl-[21px] max-md:max-w-full max-md:pl-5">
                            <p className="text-[#58585E] text-sm font-medium">Escoge el canal por el cual se comunicará el agente</p>
                            <div className="mt-[41px] max-md:max-w-full max-md:mt-10">
                                <div className="gap-5 flex max-md:flex-col max-md:items-stretch">
                                    <div className="w-[40%] max-md:w-full max-md:ml-0">
                                        <ConnectionCard
                                            title="WhatsApp"
                                            icon="https://api.builder.io/api/v1/image/assets/TEMP/01ccdc6c3fd7ce600c3d17508a68419ef268a4da?placeholderIfAbsent=true"
                                            status="pending"
                                            onConnect={handleWhatsAppConnect}
                                            isSelected={selectedConnection === 'whatsapp'}
                                        />
                                    </div>
                                    <div className="w-[60%] ml-5 max-md:w-full max-md:ml-0">
                                        <ConnectionCard
                                            title="Chat GPT"
                                            icon="https://api.builder.io/api/v1/image/assets/TEMP/0f9006732fc6f1593fd2bc535dd9f689c798925c?placeholderIfAbsent=true"
                                            status="connected"
                                            onConnect={handleChatGPTConnect}
                                            isSelected={selectedConnection === 'chatgpt'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
                {/** Right Section */}
                <div className="w-[58%] ml-5 max-md:w-full max-md:ml-0 max-md:flex-1">
                    {/* Divider */}
                    {selectedConnection === 'whatsapp' ? (
                        <QRSection />
                    ) : (
                        <section className="flex grow items-stretch gap-[40px_75px] flex-wrap max-md:max-w-full max-md:mt-10">
                            <div className="aspect-[0] object-contain w-px shrink-0 bg-gray-300 sm:hidden" />
                            <form className="w-full max-w-md mx-auto" onSubmit={handleSubmit}>
                                <h2 className="text-[#58585E] text-2xl font-bold mb-6">Open IA</h2>
                                <div className="mb-4">
                                    <label className="block text-[#58585E] text-sm font-medium mb-2" htmlFor="apiKey">API Key</label>
                                    <input
                                        id="apiKey"
                                        type="text"
                                        value={apiKey}
                                        onChange={e => setApiKey(e.target.value)}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00FF7A]"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-[#58585E] text-sm font-medium mb-2" htmlFor="orgId">Organization ID</label>
                                    <input
                                        id="orgId"
                                        type="text"
                                        value={orgId}
                                        onChange={e => setOrgId(e.target.value)}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00FF7A]"
                                        required
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-[#58585E] text-sm font-medium mb-2" htmlFor="baseUrl">Base URL</label>
                                    <input
                                        id="baseUrl"
                                        type="text"
                                        value={baseUrl}
                                        onChange={e => setBaseUrl(e.target.value)}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00FF7A]"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-[#00FF7A] text-[#2B3A51] font-bold py-2 rounded-md hover:bg-[#00e06b] transition-colors">
                                    Conectar
                                </button>
                            </form>
                        </section>
                    )}
                </div>
            </div>
        </main>
    );
}
export default AgentConnect;
